import { useEffect, useState, useRef } from "react";
import Loader from "components/utils/Loading";
import { Grid, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import { STORAGE_KEYS, KEYBOARD_KEYS, APP_SETTING_KEYS } from "const";
import ShopService from "services/ShopService";
import FormTextField from "components/utils/CustomFields/FormTextField";
import toast from "react-hot-toast";
import EstimateService from "services/EstimateService";
import * as TerminalHelper from "helpers/TermialHelper";
import * as AppSettingsHelper from "helpers/AppSettingsHelper";
import { navigateToNextPosStep } from "helpers/NavigationHelper";

async function persistBillNumberSettings(prefix, suffix) {
  const currentPrefix = (await AppSettingsHelper.getSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX)) || "";
  const isPrefixChanged = prefix !== currentPrefix;

  if (isPrefixChanged) {
    await TerminalHelper.updateTerminalSettings({ billNumberPrefix: prefix });
    await AppSettingsHelper.saveSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX, prefix);
  }

  await AppSettingsHelper.saveNextBillNumber(suffix);

  const highestEstimateNo = await EstimateService.getHighestEstimateNo();

  localStorage.setItem(STORAGE_KEYS.ESTIMATE_BILL_NUMBER_SUFFIX, String(highestEstimateNo));
}

function BillNumberHeader({ onClose }) {
  return (
    <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="flex-start">
      <Grid container rowSpacing={1}>
        <Grid item xs={12} display="flex">
          <span className={styles.head1}>
            Bill-Number <span className={styles.head2}>Setup</span>
          </span>
        </Grid>
        <Grid item xs={12} className={styles.text}>
          <span>Please set the next bill number for your next sale.</span>
        </Grid>
      </Grid>
      {onClose && <CloseIcon className={styles.closeIcon} onClick={onClose} style={{ cursor: "pointer" }} />}
    </Grid>
  );
}

function BillNumberPrefixField({ billNoPrefix, setBillNoPrefix, billNo, onKeyDown, prefixRef }) {
  return (
    <Grid item xs={12}>
      <FormTextField
        id="prefix"
        label="Bill number prefix"
        value={billNoPrefix || ""}
        disabled={billNo}
        onChange={(e) => setBillNoPrefix(e.target.value)}
        onKeyDown={onKeyDown}
        inputRef={prefixRef}
      />
    </Grid>
  );
}

function BillNumberSuffixField({ billNoSuffix, setBillNoSuffix, billNo }) {
  if (!billNo) return null;
  return (
    <Grid item xs={12} style={{ paddingTop: "12px" }}>
      <FormTextField
        id="number"
        label="Next bill number"
        value={billNoSuffix || ""}
        disabled
        onChange={(e) => setBillNoSuffix(e.target.value)}
      />
    </Grid>
  );
}

function BillNumberFormFields(props) {
  return (
    <Grid item xs={12} style={{ paddingTop: "16px" }}>
      <Grid container rowSpacing={2}>
        <BillNumberPrefixField {...props} />
        <BillNumberSuffixField {...props} />
      </Grid>
    </Grid>
  );
}

function BillNumberActionButtons({ billNo, billNoPrefix, onNext, onSubmit }) {
  const btnText = billNo ? "Submit" : "Next";
  const clickHandler = billNo ? onSubmit : onNext;
  return (
    <Grid item xs={12} style={{ paddingTop: "20px" }}>
      <Button
        variant="contained"
        className={styles.btn}
        onClick={clickHandler}
        fullWidth
        disabled={!billNo && !billNoPrefix}
      >
        {btnText}
      </Button>
    </Grid>
  );
}

function useBillNumberState() {
  const [isLoading, setIsLoading] = useState(false);
  const [billNoPrefix, setBillNoPrefix] = useState(null);
  const [billNoSuffix, setBillNoSuffix] = useState(null);
  const [billNo, setBillNo] = useState(false);
  const prefixRef = useRef(null);

  useEffect(() => {
    if (prefixRef.current) prefixRef.current.focus();
    AppSettingsHelper.getSetting(APP_SETTING_KEYS.BILL_NUMBER_PREFIX).then((stored) => {
      setBillNoPrefix(stored);
    });
  }, []);

  return {
    isLoading,
    setIsLoading,
    billNoPrefix,
    setBillNoPrefix,
    billNoSuffix,
    setBillNoSuffix,
    billNo,
    setBillNo,
    prefixRef,
  };
}

async function fetchHighestBillNumberFromServer(state) {
  if (!state.billNoPrefix) return;
  state.setIsLoading(true);
  try {
    const resp = await ShopService.getHeighestBillNumberFromServer({ prefix: state.billNoPrefix });
    state.setBillNoSuffix(resp?.highest_bill_no + 1);
    state.setBillNo(true);
  } catch (error) {
    toast.error(error.message || "Failed to fetch bill number");
  } finally {
    state.setIsLoading(false);
  }
}

async function saveBillNumberSettings(state, onSuccess, navigate) {
  if (!state.billNoPrefix || !state.billNoSuffix) return toast.error("Fields cannot be empty..");
  state.setIsLoading(true);
  try {
    await persistBillNumberSettings(state.billNoPrefix, state.billNoSuffix);
    if (onSuccess) onSuccess({ prefix: state.billNoPrefix, suffix: state.billNoSuffix });
    else await navigateToNextPosStep(navigate);
  } catch (err) {
    toast.error(err.message || "Failed to update bill number");
  } finally {
    state.setIsLoading(false);
  }
}

function useBillNumberActions(state, onSuccess) {
  const navigate = useNavigate();
  return {
    fetchHighestBillNumber: () => fetchHighestBillNumberFromServer(state),
    handleSave: () => saveBillNumberSettings(state, onSuccess, navigate),
  };
}

function useBillNumberFlow(onSuccess) {
  const state = useBillNumberState();
  const { fetchHighestBillNumber, handleSave } = useBillNumberActions(state, onSuccess);

  const handleKeyDown = (e) => {
    if (e.key === KEYBOARD_KEYS.ENTER) {
      if (!state.billNo) fetchHighestBillNumber();
      else handleSave();
    }
  };

  return {
    ...state,
    fetchHighestBillNumber,
    handleSave,
    handleKeyDown,
  };
}

function BillNumberFormBody({ flowProps, onClose }) {
  return (
    <Grid container rowSpacing={3} className={styles.textArea}>
      <BillNumberHeader onClose={onClose} />
      <BillNumberFormFields
        billNoPrefix={flowProps.billNoPrefix}
        setBillNoPrefix={flowProps.setBillNoPrefix}
        billNoSuffix={flowProps.billNoSuffix}
        setBillNoSuffix={flowProps.setBillNoSuffix}
        billNo={flowProps.billNo}
        onKeyDown={flowProps.handleKeyDown}
        prefixRef={flowProps.prefixRef}
      />
      <BillNumberActionButtons
        billNo={flowProps.billNo}
        billNoPrefix={flowProps.billNoPrefix}
        onNext={flowProps.fetchHighestBillNumber}
        onSubmit={flowProps.handleSave}
      />
    </Grid>
  );
}

export default function InvoiceNumberSetUpForm({ onClose, onSuccess }) {
  const flowProps = useBillNumberFlow(onSuccess);

  return (
    <>
      <Loader isOpen={flowProps.isLoading} backdropSx={{ borderRadius: "16px" }} />
      <BillNumberFormBody flowProps={flowProps} onClose={onClose} />
    </>
  );
}
